import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  alpha,
} from '@mui/material';
import { useState } from 'react';

import { Brush01Icon } from '@/assets/icons/Brush01Icon';
import { Droplets03Icon } from '@/assets/icons/Droplets03Icon';
import { Home03Icon } from '@/assets/icons/Home03Icon';
import { Lightbulb01Icon } from '@/assets/icons/Lightbulb01Icon';
import { PaintIcon } from '@/assets/icons/PaintIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Settings01Icon } from '@/assets/icons/Settings01Icon';
import { Star01Icon } from '@/assets/icons/Star01Icon';
import { Tool01Icon } from '@/assets/icons/Tool01Icon';
import { Truck01Icon } from '@/assets/icons/Truck01Icon';
import { Users02Icon } from '@/assets/icons/Users02Icon';

interface SaveTogetherFirstTimeExperienceProps {
  onCreateGroup: () => void;
  canCreateGroup: boolean;
}

interface ServiceExample {
  title: string;
  icon: React.ReactNode;
  description: string;
  estimatedSavings: string;
  category: string;
}

const serviceExamples: ServiceExample[] = [
  {
    title: 'Hot Water Heater',
    icon: <Droplets03Icon sx={{ width: 24, height: 24 }} />,
    description: 'Group purchase and installation of water heaters',
    estimatedSavings: '20-30%',
    category: 'Utilities',
  },
  {
    title: 'Driveway Sealing',
    icon: <Home03Icon sx={{ width: 24, height: 24 }} />,
    description: 'Seal multiple driveways in the same neighborhood',
    estimatedSavings: '15-25%',
    category: 'Maintenance',
  },
  {
    title: 'Cleaning Services',
    icon: <Brush01Icon sx={{ width: 24, height: 24 }} />,
    description: 'Regular home cleaning for multiple units',
    estimatedSavings: '10-20%',
    category: 'Services',
  },
  {
    title: 'HVAC Maintenance',
    icon: <Tool01Icon sx={{ width: 24, height: 24 }} />,
    description: 'Annual HVAC service and filter replacement',
    estimatedSavings: '25-35%',
    category: 'Maintenance',
  },
  {
    title: 'Landscaping',
    icon: <Settings01Icon sx={{ width: 24, height: 24 }} />,
    description: 'Lawn care and garden maintenance',
    estimatedSavings: '15-30%',
    category: 'Services',
  },
  {
    title: 'Painting Services',
    icon: <PaintIcon sx={{ width: 24, height: 24 }} />,
    description: 'Interior or exterior painting projects',
    estimatedSavings: '20-25%',
    category: 'Maintenance',
  },
];

const SaveTogetherFirstTimeExperience: React.FC<SaveTogetherFirstTimeExperienceProps> = ({
  onCreateGroup,
  canCreateGroup,
}) => {
  const [selectedExample, setSelectedExample] = useState<string | null>(null);

  const getCategoryColor = (category: string) => {
    switch (category) {
    case 'Utilities':
      return '#f44336';
    case 'Maintenance':
      return '#ff9800';
    case 'Services':
      return '#4caf50';
    default:
      return '#9e9e9e';
    }
  };

  return (
    <Stack spacing={4} sx={{ py: 2 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: 'center' }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Users02Icon sx={{ width: 48, height: 48, color: 'primary.main' }} />
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Save Together
          </Typography>
        </Stack>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
          Join forces with your neighbors to unlock group discounts
        </Typography>
        <Typography variant="body1" sx={{ color: 'secondary.700', maxWidth: 600, mx: 'auto' }}>
          Create or join groups to negotiate better rates on services and maintenance. The more
          neighbors that join, the bigger the savings!
        </Typography>
      </Box>

      {/* How It Works */}
      <Card
        elevation={0}
        sx={{
          bgcolor: 'secondary.50',
          border: '1px solid',
          borderColor: 'secondary.200',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'secondary.900', mb: 3, textAlign: 'center' }}
          >
            How It Works
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                  }}
                >
                  1
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Create or Join a Group
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Start a new group for a service you need or join an existing one
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                  }}
                >
                  2
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Gather Neighbors
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Share with your community and reach the minimum group size
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    bgcolor: 'primary.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    fontWeight: 700,
                    fontSize: '1.25rem',
                  }}
                >
                  3
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Save Together
                </Typography>
                <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                  Unlock group rates and save money on services you already need
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Service Examples */}
      <Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: 'secondary.900', mb: 3, textAlign: 'center' }}
        >
          Popular Group Services
        </Typography>
        <Grid container spacing={2}>
          {serviceExamples.map((example) => (
            <Grid item xs={12} sm={6} md={4} key={example.title}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  border: '2px solid',
                  borderColor:
                    selectedExample === example.title ? 'primary.main' : 'divider',
                  transform: selectedExample === example.title ? 'scale(1.02)' : 'scale(1)',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'scale(1.02)',
                    boxShadow: 2,
                  },
                }}
                onClick={() => setSelectedExample(example.title)}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1.5,
                          bgcolor: alpha(getCategoryColor(example.category), 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: getCategoryColor(example.category),
                        }}
                      >
                        {example.icon}
                      </Box>
                      <Chip
                        label={example.estimatedSavings}
                        size="small"
                        sx={{
                          bgcolor: 'success.100',
                          color: 'success.800',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Stack>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, color: 'secondary.900', mb: 0.5 }}
                      >
                        {example.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'secondary.600', lineHeight: 1.5 }}>
                        {example.description}
                      </Typography>
                    </Box>
                    <Chip
                      label={example.category}
                      size="small"
                      sx={{
                        alignSelf: 'flex-start',
                        bgcolor: alpha(getCategoryColor(example.category), 0.1),
                        color: getCategoryColor(example.category),
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Benefits */}
      <Card
        elevation={0}
        sx={{
          bgcolor: 'primary.50',
          border: '1px solid',
          borderColor: 'primary.200',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: 'secondary.900', textAlign: 'center' }}
            >
              Why Save Together?
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={1} alignItems="center" textAlign="center">
                  <Truck01Icon sx={{ width: 32, height: 32, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Volume Discounts
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                    Service providers offer better rates for bulk orders
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={1} alignItems="center" textAlign="center">
                  <Users02Icon sx={{ width: 32, height: 32, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Community Power
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                    Negotiate as a group for better terms and quality
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={1} alignItems="center" textAlign="center">
                  <Lightbulb01Icon sx={{ width: 32, height: 32, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Smart Timing
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                    Schedule services when it's most convenient and cost-effective
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack spacing={1} alignItems="center" textAlign="center">
                  <Star01Icon sx={{ width: 32, height: 32, color: 'primary.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Quality Assurance
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'secondary.600' }}>
                    Vetted providers with community feedback and reviews
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.900', mb: 2 }}>
          Ready to start saving?
        </Typography>
        <Button
          startIcon={<PlusIcon />}
          variant="contained"
          size="large"
          onClick={onCreateGroup}
          disabled={!canCreateGroup}
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            fontWeight: 600,
            px: 4,
            py: 1.75,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.25)',
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: '0px 6px 16px rgba(157, 21, 0, 0.35)',
            },
            '&.Mui-disabled': {
              bgcolor: 'grey.300',
              color: 'grey.500',
            },
          }}
        >
          Create Your First Group
        </Button>
        <Typography variant="body2" sx={{ color: 'secondary.600', mt: 2 }}>
          Or browse existing groups in your community
        </Typography>
      </Box>
    </Stack>
  );
};

export default SaveTogetherFirstTimeExperience;